// src/components/organizer/OrganizerQuickActionsWidget.js
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  Grid,
  Typography,
  Box,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  People as PeopleIcon,
  Email as EmailIcon,
  Assessment as ReportIcon,
  Campaign as PromoteIcon,
  Event as EventIcon,
  Notifications as NotificationIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  PhotoCamera as PhotoIcon,
  Schedule as ScheduleIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';

const OrganizerQuickActionsWidget = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');

  const quickActions = [
    {
      title: 'Crear Evento',
      description: 'Nuevo evento deportivo',
      icon: <AddIcon />,
      color: 'primary',
      action: () => handleAction('create_event')
    },
    {
      title: 'Gestionar Inscripciones',
      description: 'Revisar participantes',
      icon: <PeopleIcon />,
      color: 'success',
      action: () => handleAction('manage_registrations')
    },
    {
      title: 'Enviar Email',
      description: 'Comunicar con participantes',
      icon: <EmailIcon />,
      color: 'info',
      action: () => handleOpenDialog('email')
    },
    {
      title: 'Generar Reporte',
      description: 'Análisis de eventos',
      icon: <ReportIcon />,
      color: 'secondary',
      action: () => handleAction('generate_report')
    }
  ];

  const communicationActions = [
    {
      title: 'Notificación',
      icon: <NotificationIcon />,
      action: () => handleAction('notification')
    },
    {
      title: 'Promocionar',
      icon: <PromoteIcon />,
      action: () => handleAction('promote')
    },
    {
      title: 'Compartir',
      icon: <ShareIcon />,
      action: () => handleAction('share')
    }
  ];

  const managementActions = [
    {
      title: 'Horarios',
      icon: <ScheduleIcon />,
      action: () => handleAction('schedule')
    },
    {
      title: 'Pagos',
      icon: <PaymentIcon />,
      action: () => handleAction('payments')
    },
    {
      title: 'Fotos',
      icon: <PhotoIcon />,
      action: () => handleAction('photos')
    }
  ];

  const handleAction = (actionType) => {
    console.log(`Ejecutando acción: ${actionType}`);
    // Aquí iría la lógica específica para cada acción
  };

  const handleOpenDialog = (type) => {
    setDialogType(type);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDialogType('');
  };

  return (
    <>
      <Card sx={{ height: '100%' }}>
        <CardHeader
          title="⚡ Acciones Rápidas"
          subheader="Herramientas de gestión"
          action={
            <Chip 
              label="Organizador" 
              color="primary" 
              size="small"
            />
          }
        />
        
        <CardContent>
          {/* Acciones principales */}
          <Typography variant="subtitle2" gutterBottom>
            🎯 Acciones Principales
          </Typography>
          
          <Grid container spacing={1} sx={{ mb: 3 }}>
            {quickActions.map((action, index) => (
              <Grid item xs={6} key={index}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={action.icon}
                  color={action.color}
                  onClick={action.action}
                  sx={{
                    height: 70,
                    flexDirection: 'column',
                    gap: 0.5,
                    '& .MuiButton-startIcon': {
                      margin: 0
                    }
                  }}
                >
                  <Typography variant="caption" fontWeight="bold">
                    {action.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                    {action.description}
                  </Typography>
                </Button>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Comunicación */}
          <Typography variant="subtitle2" gutterBottom>
            📢 Comunicación
          </Typography>
          
          <Grid container spacing={1} sx={{ mb: 3 }}>
            {communicationActions.map((action, index) => (
              <Grid item xs={4} key={index}>
                <Tooltip title={action.title}>
                  <IconButton
                    onClick={action.action}
                    sx={{
                      width: '100%',
                      height: 50,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      flexDirection: 'column',
                      gap: 0.5
                    }}
                  >
                    {action.icon}
                    <Typography variant="caption" fontSize="0.6rem">
                      {action.title}
                    </Typography>
                  </IconButton>
                </Tooltip>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Gestión */}
          <Typography variant="subtitle2" gutterBottom>
            🔧 Gestión
          </Typography>
          
          <Grid container spacing={1}>
            {managementActions.map((action, index) => (
              <Grid item xs={4} key={index}>
                <Tooltip title={action.title}>
                  <IconButton
                    onClick={action.action}
                    sx={{
                      width: '100%',
                      height: 50,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      flexDirection: 'column',
                      gap: 0.5
                    }}
                  >
                    {action.icon}
                    <Typography variant="caption" fontSize="0.6rem">
                      {action.title}
                    </Typography>
                  </IconButton>
                </Tooltip>
              </Grid>
            ))}
          </Grid>

          {/* Tip */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              💡 Tip: Usa Ctrl+N para crear un evento rápidamente
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Dialog para enviar email */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          📧 Enviar Email a Participantes
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Evento</InputLabel>
            <Select defaultValue="">
              <MenuItem value="marathon">Maratón Ciudad</MenuItem>
              <MenuItem value="copa">Copa Juvenil</MenuItem>
              <MenuItem value="triathlon">Triatlón Regional</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            label="Asunto"
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Mensaje"
            placeholder="Escribe tu mensaje para los participantes..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancelar
          </Button>
          <Button variant="contained">
            Enviar Email
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrganizerQuickActionsWidget;